import React from "react";
import {ActionRowDropdownButton, DropdownItem, DropdownItemGroup, DropdownItemGroups, Icon} from "peggirkit";
import {CheckIcon, FunnelIcon} from "@heroicons/react/24/outline";
import {FormattedMessage, IntlShape} from "react-intl";
import {SetURLSearchParams} from "react-router-dom";
import {ApplyTooltipFn} from "@widgets/tooltip/TooltipProvider";
import {JobApplicationsFilterStatus} from "@widgets/jobApplications/overview/JobApplications";


const jobApplicationStatusFilterParam = "status";
const jobApplicationStatusFilterParamOpen = "open";
const jobApplicationStatusFilterParamInProgress = "in-progress";
const jobApplicationStatusFilterParamClosed = "closed";

export const searchParamsToStatusFilter = (searchParams: URLSearchParams): JobApplicationsFilterStatus => {
    const searchParam = searchParams.get(jobApplicationStatusFilterParam);
    switch (searchParam) {
        case jobApplicationStatusFilterParamOpen:
            return JobApplicationsFilterStatus.OPEN;
        case jobApplicationStatusFilterParamInProgress:
            return JobApplicationsFilterStatus.IN_PROGRESS;
        case jobApplicationStatusFilterParamClosed:
            return JobApplicationsFilterStatus.CLOSED;
        default:
            return JobApplicationsFilterStatus.ALL;
    }
};

const setStatusFilter = (updated: JobApplicationsFilterStatus, setSearchParams: SetURLSearchParams) => {
    setSearchParams(p => {
        switch (updated) {
            case JobApplicationsFilterStatus.ALL:
                p.delete(jobApplicationStatusFilterParam);
                break;
            case JobApplicationsFilterStatus.OPEN:
                p.set(jobApplicationStatusFilterParam, jobApplicationStatusFilterParamOpen);
                break;
            case JobApplicationsFilterStatus.IN_PROGRESS:
                p.set(jobApplicationStatusFilterParam, jobApplicationStatusFilterParamInProgress);
                break;
            case JobApplicationsFilterStatus.CLOSED:
                p.set(jobApplicationStatusFilterParam, jobApplicationStatusFilterParamClosed);
                break;
        }

        return p;
    })
};

type Props = {
    statusFilter: JobApplicationsFilterStatus,
    setSearchParams: SetURLSearchParams,
    applyTooltip: ApplyTooltipFn,
    intl: IntlShape,
};

const jobApplicationStatusFilter = ({statusFilter, setSearchParams, applyTooltip, intl}: Props) => {
    return (
        <ActionRowDropdownButton
            showInRowBreakpoint={"md"}
            color={"secondary"}
            iconBefore={<Icon icon={FunnelIcon}/>}
            props={{
                ...applyTooltip(intl.formatMessage({id: "filterOnStatus"})),
            }}
            items={
                <DropdownItemGroups>
                    <DropdownItemGroup>
                        <DropdownItem
                            icon={statusFilter === JobApplicationsFilterStatus.ALL ? <Icon icon={CheckIcon}/> : "empty"}
                            disabled={statusFilter === JobApplicationsFilterStatus.ALL}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: () => setStatusFilter(JobApplicationsFilterStatus.ALL, setSearchParams),
                                },
                            }}
                        >
                            <FormattedMessage id={"showAll"}/>
                        </DropdownItem>
                    </DropdownItemGroup>

                    <DropdownItemGroup>
                        <DropdownItem
                            icon={
                                statusFilter === JobApplicationsFilterStatus.OPEN
                                    ? <Icon icon={CheckIcon}/>
                                    : "empty"
                            }
                            disabled={statusFilter === JobApplicationsFilterStatus.OPEN}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: () => setStatusFilter(JobApplicationsFilterStatus.OPEN, setSearchParams),
                                },
                            }}
                        >
                            <FormattedMessage id={"statusOpen"}/>
                        </DropdownItem>
                        <DropdownItem
                            icon={
                                statusFilter === JobApplicationsFilterStatus.IN_PROGRESS
                                    ? <Icon icon={CheckIcon}/>
                                    : "empty"
                            }
                            disabled={statusFilter === JobApplicationsFilterStatus.IN_PROGRESS}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: () => setStatusFilter(JobApplicationsFilterStatus.IN_PROGRESS, setSearchParams),
                                },
                            }}
                        >
                            <FormattedMessage id={"statusInProgress"}/>
                        </DropdownItem>
                        <DropdownItem
                            icon={
                                statusFilter === JobApplicationsFilterStatus.CLOSED
                                    ? <Icon icon={CheckIcon}/>
                                    : "empty"
                            }
                            disabled={statusFilter === JobApplicationsFilterStatus.CLOSED}
                            buttonAction={{
                                as: "button",
                                actionProps: {
                                    onClick: () => setStatusFilter(JobApplicationsFilterStatus.CLOSED, setSearchParams),
                                },
                            }}
                        >
                            <FormattedMessage id={"statusClosed"}/>
                        </DropdownItem>
                    </DropdownItemGroup>
                </DropdownItemGroups>
            }
            selected={statusFilter !== JobApplicationsFilterStatus.ALL}
        >
            {statusFilter === JobApplicationsFilterStatus.ALL && <FormattedMessage id={"showAll"}/>}
            {statusFilter === JobApplicationsFilterStatus.OPEN && <FormattedMessage id={"statusOpen"}/>}
            {statusFilter === JobApplicationsFilterStatus.IN_PROGRESS && <FormattedMessage id={"statusInProgress"}/>}
            {statusFilter === JobApplicationsFilterStatus.CLOSED && <FormattedMessage id={"statusClosed"}/>}
        </ActionRowDropdownButton>
    );
};

export default jobApplicationStatusFilter;